import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import endpoints from '../../api/endpoints';
import api from '../../api/axios';

type ShiftOption = { id: number; label?: string; value?: string; [key: string]: any };

interface ShiftLookupState {
  shiftOptions: ShiftOption[];
  loading: boolean;
  error?: string | null;
}

const initialState: ShiftLookupState = {
  shiftOptions: [],
  loading: false,
  error: null,
};

/**
 * Fetch shift lookup from backend. Optional `nuid` will be sent as `x-nuid` header.
 */
export const fetchShiftLookup = createAsyncThunk(
  'shiftLookup/fetchShiftLookup',
  async (
    { nuid }: { nuid?: string | null },
    { rejectWithValue }
  ) => {
    try {
      const response = await api.get(endpoints.ubt.testsOfChange.fetchShiftLookup, {
        headers: {
          accept: 'application/json',
          ...(nuid ? { 'x-nuid': nuid } : {}),
        },
      });

      const data = await response.data;

      // Normalize common possible shapes
      const shiftOptions: ShiftOption[] = data?.shiftOptions ?? data?.shifts ?? data?.shiftLookup ?? data ?? [];

      return { shiftOptions };
    } catch (err: any) {
      return rejectWithValue(err?.message ?? String(err));
    }
  }
);

const slice = createSlice({
  name: 'shiftLookup',
  initialState,
  reducers: {
    clearShiftLookupState(state) {
      state.shiftOptions = [];
      state.loading = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchShiftLookup.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchShiftLookup.fulfilled, (state, action: PayloadAction<{ shiftOptions: ShiftOption[] }>) => {
        state.loading = false;
        state.error = null;
        state.shiftOptions = action.payload.shiftOptions ?? [];
      })
      .addCase(fetchShiftLookup.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) ?? action.error?.message ?? 'Failed to fetch shift lookup';
      });
  },
});

export const { clearShiftLookupState } = slice.actions;

export const selectShiftOptions = (state: any) => state.shiftLookup?.shiftOptions ?? [];
export const selectShiftLookupLoading = (state: any) => state.shiftLookup?.loading ?? false;
export const selectShiftLookupError = (state: any) => state.shiftLookup?.error ?? null;

export default slice.reducer;
